import type { IEquipmentServiceClient } from '~/services/interfaces/IEquipmentServiceClient';
import type { ICatalogPanel, ICatalogPanelPage, ICatalogPanelQuery } from '~/types/equipment';
import { DEFAULT_PANEL } from '~/utils/equipment';

const standInUuid = (n: number) => `00000000-0000-4000-8000-${String(n).padStart(12, '0')}`;

/**
 * A local stand-in for the equipment catalog, so the console runs offline: Auto-Approval's default panel and a few
 * records shaped like the catalog's. Their figures are close to the datasheets but not checked against them.
 */
export const STAND_IN_PANELS: ICatalogPanel[] = [
    DEFAULT_PANEL,
    {
        uuid: standInUuid(1),
        manufacturer_name: 'REC Group',
        series: 'Alpha Pure-R',
        model_number: 'REC420AA Pure-R',
        rated_power: 420,
        efficiency: 21.7,
        length: 1730,
        width: 1118,
        open_circuit_voltage_voc: 49.4,
        optimum_operating_voltage_vmp: 41.1,
        temperature_coefficient: -0.24,
    },
    {
        uuid: standInUuid(2),
        manufacturer_name: 'Qcells',
        series: 'Q.TRON',
        model_number: 'BLK M-G2+ 425',
        rated_power: 425,
        efficiency: 21.8,
        length: 1722,
        width: 1134,
        open_circuit_voltage_voc: 39.2,
        optimum_operating_voltage_vmp: 32.6,
        temperature_coefficient: -0.24,
    },
    {
        uuid: standInUuid(3),
        manufacturer_name: 'Qcells',
        series: 'Q.PEAK DUO',
        model_number: 'BLK ML-G10+ 400',
        rated_power: 400,
        efficiency: 20.4,
        length: 1879,
        width: 1045,
        open_circuit_voltage_voc: 45.3,
        optimum_operating_voltage_vmp: 37.1,
        temperature_coefficient: -0.27,
    },
    {
        uuid: standInUuid(4),
        manufacturer_name: 'Panasonic',
        series: 'EverVolt',
        model_number: 'EVPV410H',
        rated_power: 410,
        efficiency: 21.0,
        length: 1722,
        width: 1134,
        open_circuit_voltage_voc: 37.9,
        optimum_operating_voltage_vmp: 31.8,
        temperature_coefficient: -0.24,
    },
    {
        uuid: standInUuid(5),
        manufacturer_name: 'Canadian Solar',
        series: 'HiKu6',
        model_number: 'CS6R-410MS',
        rated_power: 410,
        efficiency: 21.0,
        length: 1722,
        width: 1134,
        open_circuit_voltage_voc: 37.2,
        optimum_operating_voltage_vmp: 31.3,
        temperature_coefficient: -0.26,
    },
    {
        uuid: standInUuid(6),
        manufacturer_name: 'Jinko Solar',
        series: 'Tiger Neo',
        model_number: 'JKM430N-54HL4-B',
        rated_power: 430,
        efficiency: 22.0,
        length: 1722,
        width: 1134,
        open_circuit_voltage_voc: 39.5,
        optimum_operating_voltage_vmp: 32.4,
        temperature_coefficient: -0.25,
    },
    {
        uuid: standInUuid(7),
        manufacturer_name: 'LONGi',
        series: 'Hi-MO 6',
        model_number: 'LR5-54HTB-430M',
        rated_power: 430,
        efficiency: 22.0,
        length: 1722,
        width: 1134,
        open_circuit_voltage_voc: 39.1,
        optimum_operating_voltage_vmp: 32.6,
        temperature_coefficient: -0.23,
    },
    {
        uuid: standInUuid(8),
        manufacturer_name: 'Maxeon',
        series: 'Maxeon 3',
        model_number: 'SPR-MAX3-400',
        rated_power: 400,
        efficiency: 22.6,
        length: 1690,
        width: 1046,
        open_circuit_voltage_voc: 75.6,
        optimum_operating_voltage_vmp: 65.8,
        temperature_coefficient: -0.27,
    },
    {
        // The catalog has records without dimensions: the console can't design with them.
        uuid: standInUuid(9),
        manufacturer_name: 'Silfab',
        series: 'Prime',
        model_number: 'SIL-400 HC+',
        rated_power: 400,
        efficiency: 21.4,
        length: null,
        width: null,
    },
];

const searchable = (panel: ICatalogPanel) =>
    [panel.manufacturer_name, panel.series, panel.model_number].filter(Boolean).join(' ').toLowerCase();

export class FakeEquipmentServiceClient implements IEquipmentServiceClient {
    readonly standIn = true;
    private panels: ICatalogPanel[] = STAND_IN_PANELS;
    private error: Error | null = null;
    readonly queries: ICatalogPanelQuery[] = [];

    setMockPanels(panels: ICatalogPanel[]): void {
        this.panels = panels;
    }

    setShouldThrowError(error: Error | null): void {
        this.error = error;
    }

    reset(): void {
        this.panels = STAND_IN_PANELS;
        this.error = null;
        this.queries.length = 0;
    }

    async listPanels(query: ICatalogPanelQuery): Promise<ICatalogPanelPage> {
        this.queries.push(query);
        if (this.error) throw this.error;
        const { search = '', page = 1, size = 12 } = query;
        const words = search.toLowerCase().split(/\s+/).filter(Boolean);
        const matching = this.panels.filter((panel) => words.every((word) => searchable(panel).includes(word)));
        return {
            total_count: matching.length,
            total_pages: Math.ceil(matching.length / size),
            result: matching.slice((page - 1) * size, page * size),
        };
    }
}
